import React, { useEffect, useMemo, useState } from "react";
import ItemModal from "../components/ItemModal";
import { useApi } from "../utils/Api";
import { Helmet } from "react-helmet-async";
import ErrorModal from "../components/ErrorModal";

const DRINKS_CATEGORY_ID = 7;

const SpinnerOverlay = () => (
  <div className="spinner-overlay">
    <div className="spinner"></div>
  </div>
);

export default function DrinksPage() {
  const { apiFetch } = useApi();

  const [menuItems, setMenuItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // [TEMP_CLOSED_NOTICE] Comment out this line to disable the closed modal
  const [showClosedModal, setShowClosedModal] = useState(true);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const formatMoney = (n) =>
    new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    }).format(Number(n || 0));

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError("");

      try {
        const data = await apiFetch("menu-items"); // same endpoint as menu
        if (!cancelled) setMenuItems(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error fetching drinks:", err);
        if (!cancelled) {
          setMenuItems([]);
          setError("Failed to load drinks. Please try again.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [apiFetch]);

  const drinks = useMemo(() => {
    return menuItems
      .filter((item) => Number(item.category_id) === DRINKS_CATEGORY_ID)
      .map((item) => ({
        id: item.id,
        name: item.item_name,
        description: item.item_description,
        price: Number(item.price),
        image: item.image,
        in_stock: item.in_stock,
        required_options: item.required_options || [],
        optional_options: item.optional_options || [],
      }));
  }, [menuItems]);

  return (
    <div className="min-h-screen bg-[#f9f9f9] py-12 font-sans">
      <Helmet>
      <title>Drinks Menu | Dee Delicious Afro-Caribbean Restaurant</title>

      <meta
        name="description"
        content="Explore refreshing drinks and Caribbean beverages at Dee Delicious Afro-Caribbean Restaurant in Liverpool."
      />

      <link rel="canonical" href="https://deedelicious.com/drinks" />

      <meta property="og:title" content="Drinks Menu | Dee Delicious Liverpool" />
      <meta property="og:description" content="Refreshing drinks and at Dee Delicious, Liverpool." />
      <meta property="og:url" content="https://deedelicious.com/drinks" />
      <meta property="og:type" content="website" />
      <meta property="og:image" content="https://deedelicious.com/static/media/logo.06d3da75fe2458d68075.png" />

      <meta name="twitter:card" content="summary_large_image" />
    </Helmet>

      {/* Spinner */}
      {loading && <SpinnerOverlay />}

      {/* [TEMP_CLOSED_NOTICE] Comment out the block below to remove the closed modal */}
      {showClosedModal && (
        <ErrorModal
          title="Notice"
          message="The restaurant is closed today. You cannot place orders at this time. We apologize for any inconvenience."
          onClose={() => setShowClosedModal(false)}
        />
      )}

      <div className="mx-auto max-w-6xl px-6 flex flex-col gap-6">
        {/* [TEMP_CLOSED_NOTICE] Comment out the block below to remove the banner */}
        <div className="bg-red-600 text-white text-center py-3 px-4 rounded-2xl font-bold text-sm md:text-base shadow-sm">
          The restaurant is closed today. You cannot place orders at this time. We apologize for any inconvenience.
        </div>

        <h1 className="text-3xl font-bold text-gray-900">Drinks</h1>

        {/* Error */}
        {!loading && error && (
          <div className="text-center text-red-500 py-16">{error}</div>
        )}

        {/* No drinks (only after loading finishes) */}
        {!loading && !error && drinks.length === 0 && (
          <div className="text-center text-gray-500 py-16">No drinks found.</div>
        )}

        {/* Items */}
        {!loading && !error && drinks.length > 0 && (
          <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {drinks.map((item) => (
              <div
                key={item.id}
                className="relative bg-[#f6f6f6] rounded-3xl overflow-hidden shadow-sm hover:shadow-md transition cursor-pointer flex flex-col"
                onClick={() => {
                  if (!item.in_stock) return;
                  setSelectedItem(item);
                  setModalOpen(true);
                }}
              >
                {item.image && (
                  <img
                    src={`https://deedelicious.com/${item.image}`}
                    alt={item.name}
                    className="w-full h-56 object-cover"
                    loading="lazy"
                  />
                )}

                {!item.in_stock && (
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center text-white text-xl font-bold rounded-3xl">
                    Out of Stock
                  </div>
                )}

                <div className="p-6 flex flex-col flex-1">
                  <div className="flex justify-between gap-3">
                    <h3 className="text-lg font-bold text-gray-900">
                      {item.name}
                    </h3>
                    <p className="text-lg font-bold text-orange-500 whitespace-nowrap">
                      {formatMoney(item.price)}
                    </p>
                  </div>

                  <p className="mt-3 text-sm text-gray-600">
                    {item.description}
                  </p>

                  <div className="mt-auto pt-6">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!item.in_stock) return;
                        setSelectedItem(item);
                        setModalOpen(true);
                      }}
                      className={`w-full flex items-center justify-center gap-2 rounded-2xl py-2 font-semibold transition ${
                        item.in_stock
                          ? "bg-primary/10 text-primary hover:bg-primary hover:text-white"
                          : "bg-gray-300 text-gray-500 cursor-not-allowed"
                      }`}
                    >
                      <span className="text-lg">＋</span>{" "}
                      {item.in_stock ? "Add to Order" : "Unavailable"}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <ItemModal
          item={selectedItem}
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
        />
      </div>
    </div>
  );
}